export const isTWA = (): boolean => {
  // TWA exposes the Digital Goods API and runs in standalone display mode
  return (
    'getDigitalGoodsService' in window &&
    window.matchMedia('(display-mode: standalone)').matches
  );
};

export const isNativeApp = (): boolean => {
  // 1. Developer override via URL query parameter
  if (process.env.NODE_ENV === 'development' && new URLSearchParams(window.location.search).get('native') === 'true') {
    return true;
  }

  // 2. TWA (Trusted Web Activity) running from Google Play
  if (isTWA()) {
    return true;
  }

  // 3. Heuristic: React Native WebView injects this global
  if ((window as any).ReactNativeWebView) {
    return true;
  }

  // 4. Capacitor / Cordova check. NOTE: @capacitor/core defines window.Capacitor in the
  // web build too, so this is true for every browser. Billing screens (shouldShowIAP)
  // rely on that today, so it stays; use isStoreApp() to tell a real store install apart.
  if ((window as any).cordova || (window as any).Capacitor) {
    return true;
  }

  // 5. Fallback: user agent contains specific markers set by the native app shell
  const ua = navigator.userAgent || navigator.vendor || '';
  return /FuelIQApp/i.test(ua);
};

/**
 * A real store install: the iPhone app (Capacitor on a native platform) or the
 * Play Store app (TWA). Unlike isNativeApp(), false in an ordinary browser.
 */
export const isStoreApp = (): boolean => {
  const cap = (window as any).Capacitor;
  return isTWA() || !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent);
};

export const isIOS = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/i.test(userAgent);
};

export const isAndroid = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android/i.test(userAgent);
};

export const isStandalonePWA = (): boolean => {
  return (
    (window.navigator as any).standalone === true || // iOS
    window.matchMedia('(display-mode: standalone)').matches // Android/Chrome
  );
};

export const shouldShowIAP = (): boolean => {
  // Show IAP for native apps, standalone PWAs, and mobile devices
  return isNativeApp() || isStandalonePWA() || isMobileDevice();
};

export const getPlatformType = (): 'native' | 'mobile-web' | 'desktop-web' => {
  if (isNativeApp()) {
    return 'native';
  } else if (isMobileDevice()) {
    return 'mobile-web';
  } else {
    return 'desktop-web';
  }
};

/**
 * True when we should show the custom CameraOverlay (live-preview with
 * scan-line UI and angle-detection guidance) vs. fall back to a native
 * file input with `capture="environment"`.
 *
 * iOS Safari is included: the scan-line and angle-guidance UX is the
 * core photo-logging experience, so we keep the overlay. iOS prompts
 * for camera once per Safari session on the first getUserMedia call;
 * the DeviceOrientation prompt is cached in sessionStorage inside
 * CameraOverlay itself. For App Store launch, the long-term answer
 * is the @capacitor/camera + @capacitor/motion plugins (one-time
 * OS-level grant) — not a Web API workaround.
 */
export const shouldUseCameraOverlay = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') return false;
  return true;
};
